import { useGetMoodsApiV1Suspense } from "@/api/__generated__/api.schema.ts";
import type { Mood } from "@/api/__generated__/models";
import { useIsMobile } from "@/hooks/use-mobile";
import { transformToPascal } from "@/utils/string";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetTrigger,
  ButtonDropdownIcon,
  Checkbox,
  Dropdown,
  DropdownCheckboxItem,
  DropdownContent,
  DropdownTrigger,
  DropdownTriggerButton,
  Icon,
  Typography,
} from "@backpac/monica";
import { forwardRef, useState } from "react";
import { useSearchParams } from "react-router";

// NOTE: Order of these moods will be used in the UI. Do not change.
export const Moods: Record<Mood["mood"], { title: string }> = {
  HAPPY: {
    title: "Happy",
  },
  DREAMY: {
    title: "Dreamy",
  },
  RELAXED: {
    title: "Relaxed",
  },
  EUPHORIC: {
    title: "Euphoric",
  },
  QUIRKY: {
    title: "Quirky",
  },
  SUSPENSE: {
    title: "Suspense",
  },
  MYSTERIOUS: {
    title: "Mysterious",
  },
  SENTIMENTAL: {
    title: "Sentimental",
  },
  SAD: {
    title: "Sad",
  },
  DARK: {
    title: "Dark",
  },
  ANGRY: {
    title: "Angry",
  },
  PEACEFUL: {
    title: "Peaceful",
  },
  SCARY: {
    title: "Scary",
  },
  FUNNY: {
    title: "Funny",
  },
  ROMANTIC: {
    title: "Romantic",
  },
  JAZZY: {
    title: "Jazzy",
  },
};

// Export the hook so it can be used elsewhere too
export function useMusicFilter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMoods = searchParams.get("mood")?.split(",").filter(Boolean);

  const onChange = (moods: string[]) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (moods.length > 0) {
      newSearchParams.set("mood", moods.join(","));
    } else {
      newSearchParams.delete("mood");
    }
    setSearchParams(newSearchParams);
  };

  return { currentMoods, onChange };
}

export function MusicMoodFilter() {
  const { currentMoods, onChange } = useMusicFilter();
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MusicMoodFilter.BottomSheet value={currentMoods} onChange={onChange} />;
  }

  return <MusicMoodFilter.Dropdown value={currentMoods} onChange={onChange} />;
}

interface MusicMoodFilterChildProps {
  value?: string[];
  onChange: (moodKeys: string[]) => void;
}

MusicMoodFilter.Dropdown = function MusicMoodFilterDropdown({ value, onChange }: MusicMoodFilterChildProps) {
  const {
    data: {
      data: { musicMoods },
    },
  } = useGetMoodsApiV1Suspense();
  const [open, setOpen] = useState(false);

  function handleChange(moodKey: string) {
    const newMoodKeys = value?.includes(moodKey) ? value.filter((m) => m !== moodKey) : [...(value ?? []), moodKey];
    onChange(newMoodKeys);
  }

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <MusicMoodFilter.DropdownTrigger currentValues={value} onClear={() => onChange([])} />
      <DropdownContent className="min-w-[240px]">
        <div className="max-h-[360px] overflow-y-auto">
          {Object.entries(Moods).map(([moodKey, { title }]) => (
            <DropdownCheckboxItem
              key={moodKey}
              checked={value?.includes(moodKey)}
              onSelect={(e) => {
                e.preventDefault();
                handleChange(moodKey);
              }}
            >
              <div className="flex items-center gap-1.5">
                <Typography size="sm" weight={600} color="textPrimary">
                  {title}
                </Typography>
                <Typography size="sm" weight={400} color="textSubtle">
                  {musicMoods.find((mood) => mood.mood === moodKey)?.count}
                </Typography>
              </div>
            </DropdownCheckboxItem>
          ))}
        </div>
      </DropdownContent>
    </Dropdown>
  );
};

MusicMoodFilter.BottomSheet = function MusicMoodFilterBottomSheet({ value, onChange }: MusicMoodFilterChildProps) {
  const {
    data: {
      data: { musicMoods },
    },
  } = useGetMoodsApiV1Suspense();
  const [open, setOpen] = useState(false);

  function handleChange(moodKey: string) {
    const newMoodKeys = value?.includes(moodKey) ? value.filter((m) => m !== moodKey) : [...(value ?? []), moodKey];
    onChange(newMoodKeys);
  }

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger asChild>
        <MusicMoodFilter.BottomSheetTrigger currentValues={value} onClear={() => onChange([])} />
      </BottomSheetTrigger>
      <BottomSheetContent>
        <BottomSheetHeader className="sr-only">
          <BottomSheetTitle>Select Mood</BottomSheetTitle>
        </BottomSheetHeader>
        <div className="flex flex-col max-h-[480px] overflow-y-auto">
          {Object.entries(Moods).map(([moodKey, { title }]) => (
            <div key={moodKey} className="flex items-center gap-3 p-3">
              <Checkbox
                className="w-5 h-5"
                checked={value?.includes(moodKey)}
                onCheckedChange={() => handleChange(moodKey)}
              />
              <button type="button" className="flex items-center gap-1.5" onClick={() => handleChange(moodKey)}>
                <Typography size="sm" weight={600} color="textPrimary">
                  {title}
                </Typography>
                <Typography size="sm" weight={400} color="textSubtle">
                  {musicMoods.find((mood) => mood.mood === moodKey)?.count}
                </Typography>
              </button>
            </div>
          ))}
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
};

interface MusicMoodFilterTriggerProps {
  currentValues?: string[];
  onClear: () => void;
}

MusicMoodFilter.DropdownTrigger = forwardRef<HTMLButtonElement, MusicMoodFilterTriggerProps>(
  function MusicMoodFilterTrigger({ currentValues, onClear }, ref) {
    if (currentValues == null) {
      return (
        <DropdownTrigger asChild>
          <DropdownTriggerButton ref={ref}>
            <Typography size="xs" weight={600} color="textPrimary">
              Mood
            </Typography>
          </DropdownTriggerButton>
        </DropdownTrigger>
      );
    }

    return (
      <div
        className="
          px-4 py-[9px]
          inline-flex items-center justify-between
          whitespace-nowrap rounded-full
          gap-1.5
          bg-textSecondary
          text-xs text-white
          font-inter font-normal
          leading-1.2
          align-middle
          transition-colors
          disabled:pointer-events-none disabled:text-borderDark
          focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <DropdownTrigger asChild>
          <Typography asChild size="xs" weight={600} className="flex items-center gap-1">
            <button type="button">
              {transformToPascal(currentValues[0])}
              {currentValues.length > 1 ? (
                <div className="flex items-center space-x-1">
                  <Typography color="textSubtle" size="sm">
                    +
                  </Typography>
                  <span>{currentValues.length - 1} more</span>
                </div>
              ) : (
                ""
              )}
            </button>
          </Typography>
        </DropdownTrigger>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center justify-center text-textPrimary rounded-full w-3 h-3"
        >
          <i className="i-bp-cancel-filled_black inline-block size-3" />
        </button>
      </div>
    );
  },
);

MusicMoodFilter.BottomSheetTrigger = forwardRef<HTMLButtonElement, MusicMoodFilterTriggerProps>(
  function MusicMoodFilterTrigger({ currentValues, onClear }, ref) {
    if (currentValues == null) {
      return (
        <BottomSheetTrigger asChild>
          <DropdownTriggerButton ref={ref} rightAddon={<ButtonDropdownIcon />}>
            <Typography size="xs" weight={600} color="textPrimary">
              Mood
            </Typography>
          </DropdownTriggerButton>
        </BottomSheetTrigger>
      );
    }

    return (
      <div
        className="
      px-4 py-[9px]
      inline-flex items-center justify-between
      whitespace-nowrap rounded-full
      gap-1.5
      bg-textSecondary
      text-xs text-white
      font-inter font-normal
      leading-1.2
      align-middle
      transition-colors
      disabled:pointer-events-none disabled:text-borderDark
      focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
    "
      >
        <BottomSheetTrigger asChild>
          <Typography asChild size="xs" weight={600} className="flex items-center gap-1">
            <button type="button">
              {currentValues[0]}
              {currentValues.length > 1 ? (
                <div className="flex items-center space-x-1">
                  <Typography color="textSubtle" size="sm">
                    +
                  </Typography>
                  <span>{currentValues.length - 1} more</span>
                </div>
              ) : (
                ""
              )}
            </button>
          </Typography>
        </BottomSheetTrigger>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center justify-center bg-white text-textPrimary rounded-full w-4 h-4"
        >
          <Icon name="close" className="size-2" />
        </button>
      </div>
    );
  },
);

MusicMoodFilter.Pending = function MusicFilterByMoodPending() {
  return (
    <Typography asChild size="xs" weight={600} color="textMuted">
      <DropdownTriggerButton>Mood</DropdownTriggerButton>
    </Typography>
  );
};
